import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

const STAGES = [
  { id: 'applied', label: 'Review' },
  { id: 'interview_scheduled', label: 'Call scheduled' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Declined' },
];

function getStageLabel(stage) {
  return (
    STAGES.find((item) => item.id === stage)?.label ||
    stage ||
    'Applied'
  );
}

function getStageStyle(stage) {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 9px',
    borderRadius: 999,
    border: '1px solid #e5e7eb',
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  };

  if (
    [
      'interview_passed',
      'quiz_passed',
      'approved',
    ].includes(stage)
  ) {
    return {
      ...base,
      background: '#ecfdf5',
      color: '#065f46',
    };
  }

  if (
    [
      'interview_failed',
      'quiz_failed',
      'rejected',
    ].includes(stage)
  ) {
    return {
      ...base,
      background: '#fef2f2',
      color: '#991b1b',
    };
  }

  if (
    [
      'module_sent',
      'module_in_progress',
      'final_interview',
    ].includes(stage)
  ) {
    return {
      ...base,
      background: '#fffbeb',
      color: '#92400e',
    };
  }

  return {
    ...base,
    background: '#f8fafc',
    color: '#111',
  };
}

function DetailRow({ label, value }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '165px 1fr',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid #f3f4f6',
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 800,
          color: '#6b7280',
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 13,
          color: '#111',
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {value || '—'}
      </div>
    </div>
  );
}

export default function ReferralPartnerApplicants() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');

  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] =
    useState(false);

  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  function adminHeaders() {
    const token =
      localStorage.getItem('adminToken') || '';

    return token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};
  }

  async function fetchApplications() {
    setLoading(true);
    setError('');

    try {
      const queryString = stageFilter
        ? `?stage=${encodeURIComponent(stageFilter)}`
        : '';

      const response = await axios.get(
       `${API}/referral-partners/admin${queryString}`,
        {
          headers: adminHeaders(),
        }
      );

      setApplications(
        response.data?.applications || []
      );
    } catch (err) {
      console.error(
        '[ReferralPartnerApplicants] fetchApplications',
        err
      );

      setError(
        err?.response?.data?.message ||
          'Failed to load referral partner applications.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function openApplicant(id) {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    setError('');

    try {
      const response = await axios.get(
       `${API}/referral-partners/admin/${id}`,
        {
          headers: adminHeaders(),
        }
      );

      const application =
        response.data?.application || null;

      setDetail(application);
      setNotes(
        String(application?.notes || '')
      );
    } catch (err) {
      console.error(
        '[ReferralPartnerApplicants] openApplicant',
        err
      );

      setError(
        err?.response?.data?.message ||
          'Failed to load application.'
      );
    } finally {
      setDetailLoading(false);
    }
  }

  function closeApplicant() {
    setSelectedId('');
    setDetail(null);
    setNotes('');
  }

  async function updateApplicant(id, patch) {
    setSaving(true);
    setError('');

    try {
      const approving = patch.stage === 'approved';
      const response = approving
        ? await axios.post(`${API}/referral-partners/admin/${id}/approve-partner`, {}, { headers: adminHeaders() })
        : await axios.patch(`${API}/referral-partners/admin/${id}`, patch, { headers: adminHeaders() });
      const updated = response.data?.application || response.data?.partner || {};

      setApplications((prev) =>
        prev.map((application) =>
          String(application._id) === String(id)
            ? {
                ...application,
                ...updated,
              }
            : application
        )
      );

      setDetail((prev) => ({
        ...(prev || {}),
        ...updated,
      }));

      if (patch.notes !== undefined) {
        setNotes(
          String(updated?.notes || '')
        );
      }
    } catch (err) {
      console.error(
        '[ReferralPartnerApplicants] updateApplicant',
        err
      );

      setError(
        err?.response?.data?.message ||
          'Failed to update application.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function deleteApplicant(id) {
    const confirmed = window.confirm(
      'Delete this referral partner application? This cannot be undone.'
    );

    if (!confirmed) return;

    setSaving(true);
    setError('');

    try {
      await axios.delete(
        `${API}/referral-partners/admin/${id}`,
        {
          headers: adminHeaders(),
        }
      );

      setApplications((prev) =>
        prev.filter(
          (application) =>
            String(application._id) !==
            String(id)
        )
      );

      if (
        String(selectedId) === String(id)
      ) {
        closeApplicant();
      }
    } catch (err) {
      console.error(
        '[ReferralPartnerApplicants] deleteApplicant',
        err
      );

      setError(
        err?.response?.data?.message ||
          'Failed to delete application.'
      );
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    fetchApplications();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageFilter]);

  const filteredApplications = useMemo(() => {
    const normalizedQuery = String(
      query || ''
    )
      .trim()
      .toLowerCase();

    if (!normalizedQuery) {
      return applications;
    }

    return applications.filter(
      (application) => {
        const fullName =
          `${application.firstName || ''} ${
            application.lastName || ''
          }`
            .trim()
            .toLowerCase();

        return (
          fullName.includes(
            normalizedQuery
          ) ||
          String(
            application.email || ''
          )
            .toLowerCase()
            .includes(normalizedQuery) ||
          String(
            application.city || ''
          )
            .toLowerCase()
            .includes(normalizedQuery) ||
          String(
            application.instagramHandle || ''
          )
            .toLowerCase()
            .includes(normalizedQuery)
        );
      }
    );
  }, [applications, query]);

  const stats = useMemo(() => {
    return {
      total: applications.length,

      newApplications:
        applications.filter(
          (application) =>
            (application.stage ||
              'applied') === 'applied'
        ).length,

      interviewing:
        applications.filter(
          (application) =>
            [
              'interview_link_sent',
              'interview_scheduled',
            ].includes(application.stage)
        ).length,

      approvedStage:
        applications.filter(
          (application) =>
            application.stage ===
            'approved'
        ).length,
    };
  }, [applications]);

  return (
    <div
      style={{
        marginTop: 14,
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 900,
              color: '#111',
            }}
          >
            Creator Recruiting
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            Review applications and move
            candidates through the referral
            partner process.
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <input
            value={query}
            onChange={(e) =>
              setQuery(e.target.value)
            }
            placeholder="Search name, email, city..."
            style={{
              height: 38,
              width: 230,
              borderRadius: 8,
              border:
                '1px solid #e5e7eb',
              padding: '0 10px',
            }}
          />

          <select
            value={stageFilter}
            onChange={(e) =>
              setStageFilter(
                e.target.value
              )
            }
            style={{
              height: 38,
              borderRadius: 8,
              border:
                '1px solid #e5e7eb',
              padding: '0 10px',
              background: '#fff',
            }}
          >
            <option value="">
              All stages
            </option>

            {STAGES.map((stage) => (
              <option
                key={stage.id}
                value={stage.id}
              >
                {stage.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={fetchApplications}
            style={{
              height: 38,
              padding: '0 12px',
              borderRadius: 8,
              border: '1px solid #111',
              background: '#111',
              color: '#fff',
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* STATS */}
      <div
        style={{
          marginTop: 14,
          display: 'grid',
          gridTemplateColumns:
            'repeat(4, minmax(120px, 1fr))',
          gap: 10,
        }}
      >
        {[
          ['Total applicants', stats.total],
          [
            'New applications',
            stats.newApplications,
          ],
          [
            'Call stage',
            stats.interviewing,
          ],
          [
            'Approved stage',
            stats.approvedStage,
          ],
        ].map(([label, value]) => (
          <div
            key={label}
            style={{
              border:
                '1px solid #e5e7eb',
              borderRadius: 10,
              padding: 12,
              background: '#fff',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: '#6b7280',
              }}
            >
              {label}
            </div>

            <div
              style={{
                marginTop: 2,
                fontSize: 24,
                fontWeight: 900,
                color: '#111',
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* ERROR */}
      {error ? (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 8,
            border:
              '1px solid #fecaca',
            background: '#fef2f2',
            color: '#991b1b',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {error}
        </div>
      ) : null}

      {/* TABLE */}
      <div
        style={{
          marginTop: 14,
          border:
            '1px solid #e5e7eb',
          borderRadius: 10,
          background: '#fff',
          overflowX: 'auto',
        }}
      >
        <table
          style={{
            width: '100%',
            minWidth: 1150,
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr
              style={{
                background: '#f9fafb',
              }}
            >
              <th style={styles.th}>
                Applicant
              </th>

              <th style={styles.th}>
                Age
              </th>

              <th style={styles.th}>
                City
              </th>

              <th style={styles.th}>
                Instagram
              </th>

              <th style={styles.th}>
                Network
              </th>

              <th style={styles.th}>
                Typical views
              </th>

              <th style={styles.th}>
                Outreach
              </th>

              <th style={styles.th}>
                Stage
              </th>

              <th style={styles.th}>
                Applied
              </th>

              <th style={styles.th}>
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={10}
                  style={styles.emptyCell}
                >
                  Loading...
                </td>
              </tr>
            ) : filteredApplications.length ===
              0 ? (
              <tr>
                <td
                  colSpan={10}
                  style={styles.emptyCell}
                >
                  No referral partner
                  applications yet.
                </td>
              </tr>
            ) : (
              filteredApplications.map(
                (application) => {
                  const fullName =
                    `${application.firstName || ''} ${
                      application.lastName || ''
                    }`.trim();

                  return (
                    <tr
                      key={application._id}
                      onClick={() =>
                        openApplicant(
                          application._id
                        )
                      }
                      style={{
                        borderTop:
                          '1px solid #eee',
                        cursor: 'pointer',
                      }}
                    >
                      <td style={styles.td}>
                        <div
                          style={{
                            fontWeight: 850,
                            color: '#111',
                          }}
                        >
                          {fullName ||
                            application.email}
                        </div>

                        <div
                          style={{
                            marginTop: 2,
                            fontSize: 11,
                            color: '#6b7280',
                          }}
                        >
                          {application.email}
                        </div>
                      </td>

                      <td style={styles.td}>
                        {application.age ??
                          '—'}
                      </td>

                      <td style={styles.td}>
                        {application.city ||
                          '—'}
                      </td>

                      <td style={styles.td}>
                        {application.instagramHandle ||
                          '—'}
                      </td>

                      <td style={styles.td}>
                        {application.laNetworkSize || application.monthlySocialInteractions ||
                          '—'}
                      </td>

                      <td style={styles.td}>
                        {application.typicalVideoViews ||
                          '—'}
                      </td>

                      <td style={styles.td}>
                        {application.outreachComfort ||
                          '—'}
                      </td>

                      <td style={styles.td}>
                        <span
                          style={getStageStyle(
                            application.stage ||
                              'applied'
                          )}
                        >
                          {getStageLabel(
                            application.stage ||
                              'applied'
                          )}
                        </span>
                      </td>

                      <td
                        style={{
                          ...styles.td,
                          fontSize: 11,
                          color: '#6b7280',
                        }}
                      >
                        {application.createdAt
                          ? new Date(
                              application.createdAt
                            ).toLocaleDateString()
                          : '—'}
                      </td>

                      <td
                        style={{
                          ...styles.td,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();

                            openApplicant(
                              application._id
                            );
                          }}
                          style={{
                            marginRight: 6,
                          }}
                        >
                          Review
                        </button>

                        <button
                          type="button"
                          disabled={saving}
                          onClick={(e) => {
                            e.stopPropagation();

                            deleteApplicant(
                              application._id
                            );
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                }
              )
            )}
          </tbody>
        </table>
      </div>

      {/* DETAIL DRAWER */}
      {selectedId ? (
        <div
          onClick={closeApplicant}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'flex-end',
            background:
              'rgba(0,0,0,0.45)',
          }}
        >
          <div
            onClick={(e) =>
              e.stopPropagation()
            }
            style={{
              width: 620,
              maxWidth: '94vw',
              height: '100%',
              overflowY: 'auto',
              background: '#fff',
              borderLeft:
                '1px solid #e5e7eb',
              padding: 18,
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent:
                  'space-between',
                alignItems: 'flex-start',
                gap: 12,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 19,
                    fontWeight: 900,
                    color: '#111',
                  }}
                >
                  {detail
                    ? `${detail.firstName || ''} ${
                        detail.lastName ||
                        ''
                      }`.trim() ||
                      detail.email
                    : 'Applicant'}
                </div>

                <div
                  style={{
                    marginTop: 2,
                    fontSize: 12,
                    color: '#6b7280',
                  }}
                >
                  {detail?.email || ''}
                </div>
              </div>

              <button
                type="button"
                onClick={closeApplicant}
              >
                Close
              </button>
            </div>

            {detailLoading ? (
              <div
                style={{
                  marginTop: 20,
                  color: '#6b7280',
                }}
              >
                Loading...
              </div>
            ) : !detail ? (
              <div
                style={{
                  marginTop: 20,
                  color: '#6b7280',
                }}
              >
                No application data.
              </div>
            ) : (
              <>
                {/* STAGE */}
                <section style={styles.section}>
                  <div
                    style={
                      styles.sectionLabel
                    }
                  >
                    STAGE
                  </div>

                  <select
                    value={
                      detail.stage ||
                      'applied'
                    }
                    disabled={saving}
                    onChange={(e) =>
                      updateApplicant(
                        detail._id,
                        {
                          stage:
                            e.target.value,
                        }
                      )
                    }
                    style={{
                      width: '100%',
                      height: 40,
                      padding: '0 10px',
                      borderRadius: 8,
                      border:
                        '1px solid #d1d5db',
                      background: '#fff',
                      fontWeight: 750,
                    }}
                  >
                    {STAGES.map(
                      (stage) => (
                        <option
                          key={stage.id}
                          value={stage.id}
                        >
                          {stage.label}
                        </option>
                      )
                    )}
                  </select>

                  <div
                    style={{
                      marginTop: 7,
                      fontSize: 11,
                      lineHeight: 1.45,
                      color: '#6b7280',
                    }}
                  >
                    Choosing Approved activates the creator and assigns their invite code. Review → call → decision.
                  </div>
                </section>

                {/* BASIC INFO */}
                <section style={styles.section}>
                  <div
                    style={
                      styles.sectionTitle
                    }
                  >
                    Basic Info
                  </div>

                  <DetailRow
                    label="Name"
                    value={`${detail.firstName || ''} ${
                      detail.lastName ||
                      ''
                    }`.trim()}
                  />

                  <DetailRow
                    label="Email"
                    value={detail.email}
                  />

                  <DetailRow
                    label="Age"
                    value={
                      detail.age != null
                        ? String(
                            detail.age
                          )
                        : ''
                    }
                  />

                  <DetailRow
                    label="City"
                    value={detail.city}
                  />

                  <DetailRow
                    label="Instagram"
                    value={
                      detail.instagramHandle
                    }
                  />

                  <DetailRow
                    label="TikTok"
                    value={
                      detail.tiktokHandle
                    }
                  />
                </section>

                <section style={styles.section}>
                  <div style={styles.sectionTitle}>Network and outreach</div>
                  <DetailRow label="Lives in LA" value={detail.livesInLosAngeles === true ? 'Yes' : detail.livesInLosAngeles === false ? 'No' : detail.city} />
                  <DetailRow label="People they could invite" value={detail.laNetworkSize || detail.monthlySocialInteractions} />
                  <DetailRow label="Personal invitations" value={detail.outreachComfort} />
                  <DetailRow label="Willing to cold DM" value={detail.coldDmWillingness === true ? 'Yes' : detail.coldDmWillingness === false ? 'No' : ''} />
                  <DetailRow label="Typical video views" value={detail.typicalVideoViews} />
                </section>

                {/* NOTES */}
                <section style={styles.section}>
                  <div
                    style={
                      styles.sectionLabel
                    }
                  >
                    INTERNAL NOTES
                  </div>

                  <textarea
                    value={notes}
                    onChange={(e) =>
                      setNotes(e.target.value)
                    }
                    placeholder="Interview notes, concerns, follow-up, etc."
                    style={{
                      width: '100%',
                      minHeight: 110,
                      padding: 10,
                      borderRadius: 8,
                      border:
                        '1px solid #d1d5db',
                      boxSizing:
                        'border-box',
                      fontFamily:
                        'inherit',
                      resize: 'vertical',
                    }}
                  />

                  <button
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      updateApplicant(
                        detail._id,
                        {
                          notes:
                            notes.trim(),
                        }
                      )
                    }
                    style={{
                      height: 36,
                      marginTop: 8,
                      padding: '0 12px',
                      borderRadius: 8,
                      border:
                        '1px solid #111',
                      background: '#111',
                      color: '#fff',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    {saving
                      ? 'Saving...'
                      : 'Save notes'}
                  </button>
                </section>

                {/* DELETE */}
                <section style={styles.section}>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      deleteApplicant(
                        detail._id
                      )
                    }
                    style={{
                      height: 36,
                      padding: '0 12px',
                      borderRadius: 8,
                      border:
                        '1px solid #fecaca',
                      background: '#fff',
                      color: '#991b1b',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    Delete application
                  </button>
                </section>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  th: {
    padding: 10,
    textAlign: 'left',
    fontSize: 11,
    color: '#4b5563',
    fontWeight: 850,
  },

  td: {
    padding: 10,
    fontSize: 12,
    color: '#111',
    verticalAlign: 'middle',
  },

  emptyCell: {
    padding: 16,
    fontSize: 12,
    color: '#6b7280',
  },

  section: {
    marginTop: 18,
    paddingTop: 16,
    borderTop: '1px solid #e5e7eb',
  },

  sectionLabel: {
    marginBottom: 8,
    fontSize: 11,
    fontWeight: 900,
    color: '#6b7280',
    letterSpacing: '0.03em',
  },

  sectionTitle: {
    marginBottom: 6,
    fontSize: 14,
    fontWeight: 900,
    color: '#111',
  },
};